import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";
import { AnalyticsService } from "../../services/analytics/analyticsService";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

interface MonthlyPoint {
  month: string;
  factures: number;
  montant: number;
  validation: number;
}

interface SectorPoint {
  name: string;
  value: number;
}

interface DetailedStats {
  factures_traitees: number;
  volume_total: number;
  taux_validation_moyen: number;
  delai_traitement_moyen: number;
}

interface GlobalStats {
  monthly_evolution: MonthlyPoint[];
  sector_distribution: SectorPoint[];
  detailed_stats: DetailedStats;
}

export const DashboardCharts: React.FC = () => {
  const { t } = useTranslation();
  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AnalyticsService.getGlobalStats()
      .then((res) => setGlobalStats(res.data))
      .catch((err) => console.error("DashboardCharts: failed to load stats", err))
      .finally(() => setLoading(false));
  }, []);

  const monthlyData: MonthlyPoint[] = globalStats?.monthly_evolution ?? [];
  const sectorData: SectorPoint[] = globalStats?.sector_distribution ?? [];
  const details: DetailedStats = globalStats?.detailed_stats ?? {
    factures_traitees: 0,
    volume_total: 0,
    taux_validation_moyen: 0,
    delai_traitement_moyen: 0,
  };

  const fmtVolume = (v: number) =>
    v >= 1_000_000
      ? `${(v / 1_000_000).toFixed(1)}M FCFA`
      : `${new Intl.NumberFormat("fr-FR").format(v)} FCFA`;

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white p-6 rounded-lg shadow animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
            <div className="h-64 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Volume mensuel */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t("dashboard.stats.monthlyEvolution")}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}M`} />
            <Tooltip
              formatter={(value: number) => [
                new Intl.NumberFormat("fr-FR").format(value),
                "FCFA",
              ]}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="montant"
              stroke="#0ea5e9"
              strokeWidth={2}
              name={t("factures.charts.monthlyVolume")}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Repartition par secteur */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t("dashboard.stats.sectorDistribution")}
        </h3>
        {sectorData.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
            {t("common.noData", "Aucune donnee disponible")}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={sectorData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={80}
                dataKey="value"
              >
                {sectorData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number) => [
                  `${value}%`,
                  t("factures.charts.part"),
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Performance de validation */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t("dashboard.stats.validationPerformance")}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis unit="%" />
            <Tooltip formatter={(v: number) => [`${v}%`]} />
            <Legend />
            <Bar
              dataKey="validation"
              fill="#10b981"
              name={t("factures.charts.validationRate")}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Statistiques detaillees */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t("dashboard.stats.detailedStats")}
        </h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
            <span className="text-sm font-medium text-gray-700">
              {t("dashboard.stats.processedInvoices")}
            </span>
            <span className="text-lg font-semibold text-gray-900">
              {details.factures_traitees}
            </span>
          </div>
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
            <span className="text-sm font-medium text-gray-700">
              {t("dashboard.stats.totalVolume")}
            </span>
            <span className="text-lg font-semibold text-gray-900">
              {fmtVolume(details.volume_total)}
            </span>
          </div>
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
            <span className="text-sm font-medium text-gray-700">
              {t("dashboard.stats.avgValidationRate")}
            </span>
            <span className="text-lg font-semibold text-success-600">
              {details.taux_validation_moyen}%
            </span>
          </div>
          <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
            <span className="text-sm font-medium text-gray-700">
              {t("dashboard.stats.avgProcessingTime")}
            </span>
            <span className="text-lg font-semibold text-gray-900">
              {details.delai_traitement_moyen} {t("dashboard.stats.days")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
